import { ChainId, Currency, Token } from "@uniswap/sdk-core"
import { NetworkAlert } from "components/NetworkAlert/NetworkAlert"
import SwapHeader from "components/swap/SwapHeader"
import { SwapTab } from "components/swap/constants"
import { PageWrapper, SwapWrapper } from "components/swap/styled"
import { useScreenSize } from "hooks/screenSize"
import { ReactNode } from "react"
import { useLocation } from "react-router-dom"
import { InterfaceTrade, TradeState } from "state/routing/types"
import { isPreviewTrade } from "state/routing/utils"
import {
  SwapAndLimitContextProvider,
  SwapContextProvider,
} from "state/swap/SwapContext"
import {
  SwapAndLimitContextProvider as WrapAndLimitContextProvider,
  SwapContextProvider as WrapContextProvider,
} from "state/wrap/SwapContext"
import { SwapAndLimitContext } from "state/swap/types" // ugly: used for mint & wrap
import { useIsDarkMode } from "../../theme/components/ThemeToggle"
import { SwapForm } from "./SwapForm"
import { WrapForm } from "./WrapForm"

export function getIsReviewableQuote(
  trade: InterfaceTrade | undefined,
  tradeState: TradeState,
  swapInputError?: ReactNode
): boolean {
  if (swapInputError) return false
  // if the current quote is a preview quote, allow the user to progress to the Swap review screen
  if (isPreviewTrade(trade)) return true

  return Boolean(trade && tradeState === TradeState.VALID)
}

export default function SwapPage({ className }: { className?: string }) {
  const location = useLocation()

  const chainId = ChainId.MAINNET
  const nutcoinInputCurrency = new Token(
    chainId,
    "0x473F4068073Cd5b2aB0E4Cc8E146F9EDC6fb52CC",
    18,
    "NUT",
    "Nutcoin"
  )
  // not used because will be overriden in SwapCurrencyInputPanel with ugly workarounds but useful to keeps uniswap logic and do minimal changes :
  const nutsOutputCurrency = new Token(
    chainId,
    "0x88266f9eb705F5282a2507A9c418821a2AC9f8BD",
    18,
    "NUTS",
    "Nuts"
  )
  const nuts404OutputCurrency = new Token(
    chainId,
    "0x25559f0aBBaf2A928239D2F419181147Cc2dAd74",
    18,
    "NUTS404",
    "Nuts404"
  )

  const shouldDisableTokenInputs = false /// false because token supported on Ethereum set by default forced at beggining

  return (
    <PageWrapper>
      <Swap
        className={className}
        chainId={chainId}
        disableTokenInputs={shouldDisableTokenInputs}
        initialInputCurrency={nutcoinInputCurrency}
        initialOutputCurrency={nutsOutputCurrency}
        initialOutputCurrencyWrap={nuts404OutputCurrency}
        syncTabToUrl={true}
      />
      {location.pathname !== "/limit" && <NetworkAlert />}
    </PageWrapper>
  )
}

/**
 * The swap component displays the swap interface, manages state for the swap, and triggers onchain swaps.
 *
 * In most cases, chainId should refer to the connected chain, i.e. `useWeb3React().chainId`.
 * However if this component is being used in a context that displays information from a different, unconnected
 * chain (e.g. the TDP), then chainId should refer to the unconnected chain.
 */
export function Swap({
  className,
  initialInputCurrency,
  initialOutputCurrency,
  initialOutputCurrencyWrap,
  chainId,
  disableTokenInputs = false,
  compact = false,
  syncTabToUrl,
}: {
  className?: string
  chainId?: ChainId
  disableTokenInputs?: boolean
  initialInputCurrency: Currency
  initialOutputCurrency: Currency
  initialOutputCurrencyWrap: Currency
  compact?: boolean
  syncTabToUrl: boolean
}) {
  const isDark = useIsDarkMode()
  const screenSize = useScreenSize()

  return (
    <SwapAndLimitContextProvider
      chainId={chainId}
      initialInputCurrency={initialInputCurrency}
      initialOutputCurrency={initialOutputCurrency}
    >
      <WrapAndLimitContextProvider
        chainId={chainId}
        initialInputCurrency={initialOutputCurrency}
        initialOutputCurrency={initialOutputCurrencyWrap}
      >
        {/* TODO: Move SwapContextProvider inside Swap tab ONLY after SwapHeader removes references to trade / autoSlippage */}
        <SwapAndLimitContext.Consumer>
          {({ currentTab }) => (
            <SwapContextProvider>
              <WrapContextProvider>
                <SwapWrapper
                  isDark={isDark}
                  className={className}
                  id="swap-page"
                >
                  <SwapHeader
                    compact={compact || !screenSize.sm}
                    syncTabToUrl={syncTabToUrl}
                  />
                  {currentTab === SwapTab.Mint && (
                    <SwapForm
                      disableTokenInputs={disableTokenInputs}
                      initialInputCurrency={initialInputCurrency}
                      initialOutputCurrency={initialOutputCurrency}
                    />
                  )}
                  {currentTab === SwapTab.Wrap && (
                    <WrapForm
                      disableTokenInputs={disableTokenInputs}
                      initialInputCurrency={initialInputCurrency}
                      initialOutputCurrency={initialOutputCurrencyWrap}
                    />
                  )}
                </SwapWrapper>
              </WrapContextProvider>
            </SwapContextProvider>
          )}
        </SwapAndLimitContext.Consumer>
      </WrapAndLimitContextProvider>
    </SwapAndLimitContextProvider>
  )
}
